import Axios from '../../configs/axios.config';
import { store } from '../../store/store';
import { 
  startExport, 
  completeExport, 
  failExport,
  EXPORT_STATUS 
} from '../../store/slices/export.slice';
import toast from 'react-hot-toast';
import { API_URL } from '../../configs/constants';

const normalizeDownloadUrl = (fileUrl) => {
  if (!fileUrl || typeof fileUrl !== 'string') return fileUrl;

  // Relative path: prefix with API base (no trailing slash issues)
  if (!/^https?:\/\//i.test(fileUrl)) {
    const base = (API_URL || '').replace(/\/+$/, '');
    const path = fileUrl.startsWith('/') ? fileUrl : `/${fileUrl}`;
    return base ? `${base}${path}` : fileUrl;
  }

  // Absolute URL from API: use as-is so export file host matches backend (avoids broken rewrites / 404 / CORS)
  return fileUrl;
};

/**
 * Export Service
 * Handles API calls for exporting data and manages export state
 */
class ExportService {
  constructor() {
    this.activeExports = new Map(); // Track active export requests
  }

  /**
   * Start an export process
   * @param {string} exportId - Unique export identifier
   * @param {Object} exportParams - Parameters for the export
   * @param {string} exportParams.endpoint - API endpoint for export (default: '/export')
   * @param {Object} exportParams.data - Data to send with the export request
   * @param {string} exportParams.method - HTTP method (default: 'POST')
   * @returns {Promise} - Export promise
   */
  async startExport(exportId, exportParams = {}) {
    const {
      endpoint = '/export',
      data = {},
      method = 'POST'
    } = exportParams;

    try {
      // Mark export as started in Redux
      store.dispatch(startExport({ exportId }));

      // Make API call
      const config = {
        method: method.toLowerCase(),
        url: endpoint,
        ...(method.toLowerCase() === 'get' ? { params: data } : { data })
      };

      const response = await Axios(config);

      // Handle successful response
      if (response.data && response.data.file_url && response.data.file_name) {
        store.dispatch(completeExport({
          exportId,
          file_url: response.data.file_url,
          file_name: response.data.file_name
        }));

        toast.success(`Export "${response.data.file_name}" is ready for download!`);
        return response.data;
      } else {
        throw new Error('Invalid response format from export API');
      }

    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Export failed';
      
      store.dispatch(failExport({
        exportId,
        error: errorMessage
      }));

      toast.error(`Export failed: ${errorMessage}`);
      throw error;
    }
  }

  /**
   * Retry a failed export
   * @param {string} exportId - Export ID to retry
   * @param {Object} exportParams - Original export parameters
   */
  async retryExport(exportId, exportParams) {
    const state = store.getState();
    const exportTask = state.export.exports[exportId];

    if (!exportTask) {
      console.error(`Export task ${exportId} not found`);
      return;
    }

    // Retry the export with the same parameters
    return this.startExport(exportId, exportParams);
  }

  /**
   * Download a completed export file
   * @param {string} fileUrl - URL of the file to download
   * @param {string} fileName - Name of the file
   */
  downloadFile(fileUrl, fileName) {
    try {
      const normalizedUrl = normalizeDownloadUrl(fileUrl);
      if (!normalizedUrl) {
        toast.error('No download URL available.');
        return;
      }

      const safeName =
        fileName && String(fileName).trim()
          ? String(fileName).trim()
          : 'export';

      // Cross-origin: <a download> is ignored; new tab reliably opens the file URL from API
      const link = document.createElement('a');
      link.href = normalizedUrl;
      link.rel = 'noopener noreferrer';
      link.target = '_blank';
      link.download = safeName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('Opening download…');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Download failed. Please try again.');
    }
  }

  /**
   * Get export status
   * @param {string} exportId - Export ID
   * @returns {Object|null} - Export status object
   */
  getExportStatus(exportId) {
    const state = store.getState();
    return state.export.exports[exportId] || null;
  }

  /**
   * Check if export is active (pending or exporting)
   * @param {string} exportId - Export ID
   * @returns {boolean}
   */
  isExportActive(exportId) {
    const exportStatus = this.getExportStatus(exportId);
    return exportStatus && [EXPORT_STATUS.PENDING, EXPORT_STATUS.EXPORTING].includes(exportStatus.status);
  }

  /**
   * Get all active exports
   * @returns {Array} - Array of active export objects
   */
  getActiveExports() {
    const state = store.getState();
    return Object.values(state.export.exports).filter(exportTask => 
      [EXPORT_STATUS.PENDING, EXPORT_STATUS.EXPORTING].includes(exportTask.status)
    );
  }

  /**
   * Get all completed exports
   * @returns {Array} - Array of completed export objects
   */
  getCompletedExports() {
    const state = store.getState();
    return Object.values(state.export.exports).filter(exportTask => 
      exportTask.status === EXPORT_STATUS.COMPLETED
    );
  }
}

// Create and export a singleton instance
const exportService = new ExportService();
export default exportService;
